import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, Row, Table } from 'react-bootstrap';
import { FaChartLine, FaEuroSign } from 'react-icons/fa';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ErrorToast } from '../../../utils/toasters';
import { ControllingCard } from './ControllingCard';
import { ContollingService } from '../ControllingService';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import TooltipOverlay from '../../../shared/components/TooltipOverlay';
import { IAgencyRevenueCampaignsStatistics, ICompany } from '../../../model/controlling/Controlling';
import { AgencyMarginsCompaniesFilter } from './AgencyMarginsCompaniesFilter';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

export interface IFiltersAgencyMarginsCompanies {
    page: number;
    perPage: number;
    id: string;
    managerId: string | undefined;
    clientId: string | undefined;
    totalPages: number;
    year: number;
}

const AGENCY_MARGINS_COLUMNS: ITableColumn[] = [
    { label: 'Client name' },
    { label: 'Total Campaigns' },
    { label: 'Campaign managers' },
    { label: 'Revenue' },
    { label: 'Margin' },
];
export function AgencyMarginsCompanies() {
    const currentYear = new Date().getFullYear();
    const [campaigns, setCampaigns] = useState<ICompany[]>([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [selfServiceCompany, setSelfServiceCompany] = useState<ICompany[]>([]);
    const [statistics, setStatistics] = useState<IAgencyRevenueCampaignsStatistics | null>(null);
    const [filters, setFilters] = useState<IFiltersAgencyMarginsCompanies>({
        page: 1,
        perPage: 15,
        id: '',
        managerId: '',
        clientId: '',
        totalPages: 0,
        year: currentYear,
    });

    const [statsLoading, setStatsLoading] = useState(true);
    const requestIdRef = React.useRef(0);

    const fetchCampaigns = (params: IFiltersAgencyMarginsCompanies, pageOverride?: number) => {
        const requestId = ++requestIdRef.current;
        setDataLoading(true);
        if (pageOverride === undefined) setStatsLoading(true);
        const page = pageOverride ?? params.page;

        ContollingService.getAgencMarginCompanies({ ...params, page })
            .then(({ data, page: responsePage, count, info }: any) => {
                if (requestId !== requestIdRef.current) return;

                const totalPages = Math.ceil(count / params.perPage);

                if (totalPages > 0 && page > totalPages) {
                    fetchCampaigns({ ...params, page: 1 });
                    return;
                }
                setCampaigns(data);
                setStatistics(info ?? null);
                const combinedData = info?.selfServiceCompany ? [info.selfServiceCompany] : data;
                setSelfServiceCompany(combinedData);

                setFilters({
                    ...params,
                    page: responsePage,
                    totalPages,
                });
            })
            .catch(err => {
                if (requestId === requestIdRef.current) ErrorToast(err);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) {
                    setDataLoading(false);
                    setStatsLoading(false);
                }
            });
    };
    const handlePageChange = (page: number) => {
        fetchCampaigns(filters, page);
    };

    useEffect(() => {
        fetchCampaigns(filters);
    }, [filters.year]);
    return (
        <>
            <div className="mb-5">
                <Row className="gap-y-4">
                    <ControllingCard
                        cardName="Total Revenue"
                        icon={<FaEuroSign />}
                        baseColor="primary"
                        total={statistics?.revenue ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                    <ControllingCard
                        cardName="Total Margin"
                        icon={<FaChartLine />}
                        baseColor="primary"
                        total={statistics?.margin ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                </Row>
            </div>
            <Card className="mb-5 p-4">
                <AgencyMarginsCompaniesFilter
                    handleState={fetchCampaigns}
                    setState={setFilters}
                    offersFilter={filters}
                />
            </Card>
            <TableLayout
                pagination={filters}
                listLength={selfServiceCompany.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'Self service company'}
                year={filters.year}
                skeletonColumns={AGENCY_MARGINS_COLUMNS}
                skeletonRows={1}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {AGENCY_MARGINS_COLUMNS.map(column => (
                                <th key={column.label}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {selfServiceCompany.map(supplier => (
                            <tr key={supplier.id}>
                                <td>
                                    <span className="text-primary">({supplier.id})</span> {supplier.displayName}
                                </td>
                                <td>{supplier.totalCampaigns ?? 0}</td>
                                <td>
                                    {supplier.assignedUsers?.length ? (
                                        <div className="d-flex align-items-center">
                                            {supplier.assignedUsers.slice(0, 3).map(user => (
                                                <TooltipOverlay
                                                    key={user.id}
                                                    content={
                                                        user.displayName ??
                                                        `${user.firstName ?? ''} ${user.lastName ?? ''}`
                                                    }
                                                    style={{ marginRight: '6px' }}
                                                >
                                                    <img
                                                        src={
                                                            user.profilePhoto?.thumbnail ?? '/icons/UserPlaceholder.png'
                                                        }
                                                        className="campaign-card-img"
                                                        alt={
                                                            user.displayName ??
                                                            `${user.firstName ?? ''} ${user.lastName ?? ''}`
                                                        }
                                                        onError={e => {
                                                            (e.target as HTMLImageElement).onerror = null;
                                                            (e.target as HTMLImageElement).src =
                                                                '/icons/UserPlaceholder.png';
                                                        }}
                                                    />
                                                </TooltipOverlay>
                                            ))}

                                            {supplier.assignedUsers.length > 3 && (
                                                <TooltipOverlay
                                                    content={
                                                        <div className="d-flex flex-column">
                                                            {supplier.assignedUsers.slice(3).map(user => (
                                                                <div
                                                                    key={user.id}
                                                                    className="d-flex align-items-center mb-1"
                                                                >
                                                                    <img
                                                                        src={
                                                                            user.profilePhoto?.thumbnail ??
                                                                            '/icons/UserPlaceholder.png'
                                                                        }
                                                                        className="campaign-card-img me-2"
                                                                        alt={
                                                                            user.displayName ??
                                                                            `${user.firstName ?? ''} ${
                                                                                user.lastName ?? ''
                                                                            }`
                                                                        }
                                                                        onError={e => {
                                                                            (e.target as HTMLImageElement).onerror =
                                                                                null;
                                                                            (e.target as HTMLImageElement).src =
                                                                                '/icons/UserPlaceholder.png';
                                                                        }}
                                                                    />
                                                                    <span>
                                                                        {user.displayName ??
                                                                            `${user.firstName ?? ''} ${
                                                                                user.lastName ?? ''
                                                                            }`}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    }
                                                >
                                                    <span className="ms-1 cursor-pointer">
                                                        +{supplier.assignedUsers.length - 3}
                                                    </span>
                                                </TooltipOverlay>
                                            )}
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>{priceSplitter(supplier.totalRevenueWithFee)} €</td>
                                <td>{priceSplitter(supplier.totalMargin)} €</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
            <TableLayout
                pagination={filters}
                listLength={campaigns.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'campaigns'}
                skeletonColumns={AGENCY_MARGINS_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {AGENCY_MARGINS_COLUMNS.map(column => (
                                <th key={column.label}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {campaigns?.length > 0 &&
                            campaigns.map((supplier: any) => (
                                <tr key={supplier.id}>
                                    <td>
                                        <span className="text-primary">({supplier.id})</span> {supplier.displayName}
                                    </td>
                                    <td>{supplier.totalCampaigns ?? 0}</td>
                                    <td>
                                        {supplier.assignedUsers?.length ? (
                                            <div className="d-flex align-items-center">
                                                {supplier.assignedUsers.slice(0, 3).map((user: any) => {
                                                    const fullName = `${user.firstName ?? ''} ${
                                                        user.lastName ?? ''
                                                    }`.trim();
                                                    const name = user.displayName || fullName;

                                                    return (
                                                        <TooltipOverlay
                                                            key={user.id}
                                                            content={name}
                                                            style={{ marginRight: '6px' }}
                                                        >
                                                            <img
                                                                src={
                                                                    user.profilePhoto?.thumbnail ??
                                                                    '/icons/UserPlaceholder.png'
                                                                }
                                                                className="campaign-card-img"
                                                                alt={name}
                                                                onError={(e: any) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = '/icons/UserPlaceholder.png';
                                                                }}
                                                            />
                                                        </TooltipOverlay>
                                                    );
                                                })}

                                                {supplier.assignedUsers.length > 3 && (
                                                    <TooltipOverlay
                                                        content={
                                                            <div className="d-flex flex-column">
                                                                {supplier.assignedUsers.slice(3).map((user: any) => {
                                                                    const fullName = `${user.firstName ?? ''} ${
                                                                        user.lastName ?? ''
                                                                    }`.trim();
                                                                    const name = user.displayName || fullName;

                                                                    return (
                                                                        <div
                                                                            key={user.id}
                                                                            className="d-flex align-items-center mb-1"
                                                                        >
                                                                            <img
                                                                                src={
                                                                                    user.profilePhoto?.thumbnail ??
                                                                                    '/icons/UserPlaceholder.png'
                                                                                }
                                                                                className="campaign-card-img me-2"
                                                                                alt={name}
                                                                                onError={(e: any) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src =
                                                                                        '/icons/UserPlaceholder.png';
                                                                                }}
                                                                            />
                                                                            <span>{name}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        }
                                                    >
                                                        <span className="ms-1 cursor-pointer">
                                                            +{supplier.assignedUsers.length - 3}
                                                        </span>
                                                    </TooltipOverlay>
                                                )}
                                            </div>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>{priceSplitter(supplier.totalRevenueWithFee)} €</td>
                                    <td>{priceSplitter(supplier.totalMargin)} €</td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
